/**
 * The component represents a dropdown menu that allow user
 * to select one of salons he owns.
 *
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { find, get, isEqual, map } from 'lodash';
import { Dropdown, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';

import withModal from '../../../lib/withModal';
import CreateSalonDialog from '../containers/CreateSalonDialog';
import { switchSalon, setIsOwner } from '../../../actions/user';
import { setSettings } from '../../../actions/salon';

import type { ModalDialogTrigger } from '../../../type';

// The query to get list of all salons of current user
export const GET_SALONS_QUERY = gql`
    query getSalons {
        viewer {
            id
            ... on Administrator {
                salons {
                    id
                    name
                    administrator {
                        id
                    }
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                        enableArchive
                    }
                    subscription {
                        active
                        subId
                        customerId
                        priceId
                        trialEndsAt
                    }
                }
            }
            ... on Master {
                salons {
                    id
                    name
                    administrator {
                        id
                    }
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                        enableArchive
                    }
                    subscription {
                        active
                        subId
                        customerId
                        priceId
                        trialEndsAt
                    }
                }
            }
        }
    }
`;

// Build the settings object Redux expects from a salon's settings.
const toSalonSettings = salon =>
    Object.assign(
        { enableArchive: get(salon, 'settings.enableArchive') },
        get(salon, 'settings.schedule'),
    );

const CreateSalonHeader = styled(
    React.forwardRef((props, ref) => (
        <div ref={ref}>
            <Dropdown.Header {...props} />
        </div>
    )),
)`
    cursor: pointer;
`;

/**
 * The button that opens dialog to create a new salon
 */
const CreateSalonButton = withModal(
    ({ open, onOpen, onClose }: ModalDialogTrigger) => (
        <CreateSalonDialog
            trigger={
                <CreateSalonHeader onClick={onOpen}>
                    <Icon name="add" color="grey" />
                    <FormattedMessage
                        id="navbar.menu.newSalon"
                        defaultMessage="Create a new salon"
                    />
                </CreateSalonHeader>
            }
            open={open}
            onClose={onClose}
        />
    ),
);

const SalonSelect = () => {
    const dispatch = useDispatch();
    const currentSalonId = useSelector(({ user }) => user.get('salon'));
    const currentSalonName = useSelector(({ user }) => user.get('salonName'));
    const settings = useSelector(({ salon }) => salon.get('settings'));
    const isMaster = useSelector(({ user }) => user.get('isMaster'));

    const { loading, error, data } = useQuery(GET_SALONS_QUERY);
    const viewer = data && data.viewer;

    // Resolve the current salon and the dropdown options from the viewer.
    let currentSalon = null;
    let options = [];
    if (!loading && !error && viewer) {
        if (currentSalonId) {
            currentSalon = find(viewer.salons, { id: currentSalonId });
            if (!currentSalon && currentSalonName) {
                currentSalon = { id: currentSalonId, name: currentSalonName };
            }
        }
        if (!currentSalon && viewer.salons.length > 0) {
            [currentSalon] = viewer.salons;
        }
        options = map(viewer.salons, salon => ({
            value: salon.id,
            text: salon.name,
        }));
    }

    // Keep Redux salon settings in sync once the current salon resolves.
    useEffect(() => {
        if (!currentSalon) return;
        const salonSettings = toSalonSettings(currentSalon);
        if (currentSalon.settings && !isEqual(settings, salonSettings)) {
            dispatch(setSettings(salonSettings));
        }
    }, [currentSalon, settings, dispatch]);

    const onChange = salonId => {
        const salon = find(viewer.salons, s => s.id === salonId);
        dispatch(setIsOwner(salon.administrator.id === viewer.id));
        dispatch(switchSalon(salonId, salon.name));
        dispatch(setSettings(toSalonSettings(currentSalon)));
    };

    return (
        <Dropdown
            trigger={
                <span>
                    <Icon name="home" />
                    {get(currentSalon, 'name')}
                </span>
            }
        >
            <Dropdown.Menu>
                {!isMaster && <CreateSalonButton />}
                <Dropdown.Menu scrolling>
                    {map(options, option => (
                        <Dropdown.Item
                            key={option.value}
                            active={
                                !!currentSalon &&
                                option.value === currentSalon.id
                            }
                            onClick={(e, { value }) => {
                                onChange(value);
                            }}
                            {...option}
                        />
                    ))}
                </Dropdown.Menu>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default SalonSelect;
